import { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import InputMask from "react-input-mask";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getSellerQuery } from "../../queries";
import TableContainer from "../Common/TableContainer";
import { bindCardToSeller, removeCardFromSeller } from "../../api/seller";
import DeleteModal from "../Common/DeleteModal";
import {
  Row,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  Col,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

export function SellerCards() {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const { sellerId } = useParams();
  const [deleteModal, setDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: sellerData, isError, error } = useQuery({
    ...getSellerQuery(sellerId),
  });

  useEffect(() => {
    if (isError) {
      alert(error?.response?.data?.error?.message || error?.message);
    }
  }, [isError, error]);

  const toggle = () => {
    setModal(!modal);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      cardNumber: "",
      terminalId: "",
    },
    validationSchema: Yup.object({
      cardNumber: Yup.number().required("Please enter card number"),
      expireDate: Yup.string().required("Please enter expire date"),
    }),
    onSubmit: (values, { resetForm }) => {
      setLoading(true);
      const newValues = {
        cardNumber: values.cardNumber.replace(/\s/g, ""),
        expireDate: values.expireDate.replace("/", ""),
      };
      bindCardToSeller(sellerId, newValues)
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ["seller", sellerId],
          });
          resetForm();
          toggle();
        })
        .catch((err) => {
          if (err?.data.error?.message || err?.data.error?.code) {
            alert(err?.data.error?.message || err?.data.error?.code);
          }
        })
        .finally(() => setLoading(false));
    },
  });

  const columns = useMemo(
    () => [
      {
        header: t("Masked number"),
        accessorKey: "maskedNumber",
        enableColumnFilter: false,
      },
      {
        header: t("Actions"),
        cell: ({ row }) => (
          <>
            <button
              className="btn btn-sm btn-danger"
              style={{ marginLeft: "10px" }}
              onClick={() => setDeleteModal(row.original.id)}
            >
              <i className="ri-delete-bin-6-fill"></i>
            </button>
          </>
        ),
      },
    ],
    []
  );

  const onDelete = () => {
    removeCardFromSeller(sellerId, deleteModal)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["seller", sellerId],
        });
      })
      .catch((err) => {
        alert(err?.response?.data?.error?.message || err?.message);
      })
      .finally(() => {
        setDeleteModal(false);
      });
  };

  return (
    <>
      <div className="card-body pt-0" style={{ padding: 0 }}  >
        <DeleteModal
          show={deleteModal}
          onDeleteClick={onDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <div>
          <Row className="g-4 mb-3 align-items-center">
            <div className="col-sm">
              <div>
                <h5 className="card-title mb-0">{t("Cards")}</h5>
              </div>
            </div>
            <div className="col-sm-auto">
              <div>
                <button
                  type="button"
                  className="btn btn-success add-btn"
                  id="create-btn"
                  onClick={() => setModal(true)}
                >
                  <i className="ri-add-line align-bottom me-1"></i>{" "}
                  {t("create")}
                </button>
              </div>
            </div>
          </Row>
          {sellerData?.data?.data?.cardNumbers?.length > 0 ? (
            <TableContainer
              columns={columns}
              data={sellerData?.data?.data?.cardNumbers || []}
              isAddUserList={false}
              customPageSize={10}
              className="custom-header-css"
              SearchPlaceholder="Search for user, email, phone, status or something..."
            />
          ) : (
            <div className="text-center p-3 text-muted">
              <h5>{t("No Data")}</h5>
            </div>
          )}
        </div>
        <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
          <ModalHeader className="bg-light p-3" toggle={toggle}>
            Add card
          </ModalHeader>
          <Form
            className="tablelist-form"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <ModalBody>
              <div className="mb-3">
                <Label>{t("Card Number")}</Label>
                <InputMask
                  mask="9999 9999 9999 9999"
                  value={validation.values.cardNumber}
                  onChange={(e) =>
                    validation.setFieldValue("cardNumber", e.target.value)
                  }
                >
                  {(inputProps) => <Input {...inputProps} type="text" />}
                </InputMask>
              </div>
              <div className="mb-3">
                <Label>{t("Expiry Date")}</Label>
                <InputMask
                  mask="99/99"
                  value={validation.values.expireDate}
                  onChange={(e) =>
                    validation.setFieldValue("expireDate", e.target.value)
                  }
                >
                  {(inputProps) => <Input {...inputProps} type="text" />}
                </InputMask>
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="hstack gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setModal(false);
                  }}
                >
                  {t("Close")}
                </button>

                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading}
                >
                  {t("create")}
                </button>
              </div>
            </ModalFooter>
          </Form>
        </Modal>
        <ToastContainer closeButton={false} limit={1} />
      </div>
    </>
  );
}
